import React, { useState, useEffect } from 'react'
import { shallow } from 'zustand/shallow'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

import TextField from '@mui/material/TextField'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined'

import { useGetLoginInfo } from '@multiversx/sdk-dapp/hooks/account'
import { useTrackTransactionStatus } from '@multiversx/sdk-dapp/hooks/transactions'

import { ZoidCore } from '@zoidpay-tech/zoidcore-web3-toolkit'
import { EsdtControlChanges } from '@zoidpay-tech/zoidcore-web3-toolkit/build/models/esdt/esdtControlChanges'
import { useChainAndNetwork } from 'src/store/chainAndNetwork'
import { mvxNetworkProvidersType } from '../../util/types'
import useBlockchainAdapter from '../../hooks/useBlockchainAdapter'
import ConnectMvxInfo from '../../components/ConnectMvxInfo'
import InfoPaper from '../../components/InfoPaper'
import { useAuthState } from '../../contexts/authContext'
import Loader from '../../components/Loader/Loader'

interface IEsdtProperties {
  canMint: { label: string; value: boolean }
  canBurn: { label: string; value: boolean }
  canPause: { label: string; value: boolean }
  canFreeze: { label: string; value: boolean }
  canWipe: { label: string; value: boolean }
  canChangeOwner: { label: string; value: boolean }
  canUpgrade: { label: string; value: boolean }
  canAddSpecialRoles: { label: string; value: boolean }
}

const defaultEsdtProperties: IEsdtProperties = {
  canMint: { label: 'Mintable', value: true },
  canBurn: { label: 'Burnable', value: true },
  canPause: { label: 'Pausable', value: true },
  canFreeze: { label: 'Freezable', value: true },
  canWipe: { label: 'Wipeable', value: true },
  canChangeOwner: { label: 'Changeable Owner', value: true },
  canUpgrade: { label: 'Upgradable', value: true },
  canAddSpecialRoles: { label: 'Can Add Special Roles', value: true },
}

const EsdtForm = () => {
  const adapter = useBlockchainAdapter()
  const { network } = useChainAndNetwork(
    (state) => ({
      network: state.network,
    }),
    shallow,
  )

  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const ticker = queryParams.get('tokenTicker')

  const canMint =
    queryParams.get('canMint') !== null
      ? queryParams.get('canMint') === 'true'
      : defaultEsdtProperties.canMint.value
  const canBurn =
    queryParams.get('canBurn') !== null
      ? queryParams.get('canBurn') === 'true'
      : defaultEsdtProperties.canBurn.value
  const canPause =
    queryParams.get('canPause') !== null
      ? queryParams.get('canPause') === 'true'
      : defaultEsdtProperties.canPause.value
  const canFreeze =
    queryParams.get('canFreeze') !== null
      ? queryParams.get('canFreeze') === 'true'
      : defaultEsdtProperties.canFreeze.value
  const canWipe =
    queryParams.get('canWipe') !== null
      ? queryParams.get('canWipe') === 'true'
      : defaultEsdtProperties.canWipe.value
  const canChangeOwner =
    queryParams.get('canChangeOwner') !== null
      ? queryParams.get('canChangeOwner') === 'true'
      : defaultEsdtProperties.canChangeOwner.value
  const canUpgrade =
    queryParams.get('canUpgrade') !== null
      ? queryParams.get('canUpgrade') === 'true'
      : defaultEsdtProperties.canUpgrade.value
  const canAddSpecialRoles =
    queryParams.get('canAddSpecialRoles') !== null
      ? queryParams.get('canAddSpecialRoles') === 'true'
      : defaultEsdtProperties.canAddSpecialRoles.value

  const [address, setAddress] = useState<string>('')
  const [tokenTicker, setTokenTicker] = useState<string>(ticker ?? '')
  const [esdtProperties, setEsdtProperties] = useState<IEsdtProperties>({
    canMint: { label: 'Mintable', value: canMint },
    canBurn: { label: 'Burnable', value: canBurn },
    canPause: { label: 'Pausable', value: canPause },
    canFreeze: { label: 'Freezable', value: canFreeze },
    canWipe: { label: 'Wipeable', value: canWipe },
    canChangeOwner: { label: 'Changeable Owner', value: canChangeOwner },
    canUpgrade: { label: 'Upgradable', value: canUpgrade },
    canAddSpecialRoles: {
      label: 'Can Add Special Roles',
      value: canAddSpecialRoles,
    },
  })
  const [disabled, setDisabled] = useState<boolean>(false)

  let url = new URL(window.location.href)
  const user = useAuthState().user
  const initializer = {
    apiKey: '',
    authorization: user?.accessToken,
    env: process.env.REACT_APP_ENVIRONMENT === 'development' ? 'debug' : '',
  }
  const zoid = new ZoidCore(initializer)

  const mvxNetworkProviders: mvxNetworkProvidersType = {
    testnet: 'https://devnet-api.multiversx.com',
    mainnet: 'https://api.multiversx.com',
  }

  const { isLoggedIn } = useGetLoginInfo()
  const navigate = useNavigate()

  useEffect(() => {
    const setAddressAfterLogin = async () => {
      const walletAddress = await adapter.multiversx?.getAddress()
      setAddress(walletAddress)
    }
    if (isLoggedIn && adapter.multiversx) {
      setAddressAfterLogin()
    }
  }, [isLoggedIn, adapter.multiversx])

  const payloadEsdtProperties = {
    canMint: esdtProperties.canMint.value,
    canBurn: esdtProperties.canBurn.value,
    canPause: esdtProperties.canPause.value,
    canFreeze: esdtProperties.canFreeze.value,
    canWipe: esdtProperties.canWipe.value,
    canChangeOwner: esdtProperties.canChangeOwner.value,
    canUpgrade: esdtProperties.canUpgrade.value,
    canAddSpecialRoles: esdtProperties.canAddSpecialRoles.value,
  }

  const mvxChangesPayload: EsdtControlChanges = {
    address,
    tokenTicker,
    networkProvider: mvxNetworkProviders[network],
    esdtProperties: payloadEsdtProperties,
  }

  const handleTokenTickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    url.searchParams.set('tokenTicker', e.target.value)
    window.history.pushState({}, '', url)
    setTokenTicker(e.target.value)
  }

  const handleEsdtPropertiesChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    property: keyof IEsdtProperties,
  ) => {
    url.searchParams.set(property, `${e.target.checked}`)
    window.history.pushState({}, '', url)
    const newEsdtProperties = { ...esdtProperties }
    newEsdtProperties[property].value = e.target.checked
    setEsdtProperties(newEsdtProperties)
  }

  const sessionPayload = sessionStorage.getItem('mvxChangesPayload')
  const sessionPayloadParsed = JSON.parse(sessionPayload || '{}')

  const transactionStatus = useTrackTransactionStatus({
    transactionId: sessionPayloadParsed?.txHash,
    onSuccess: async () => {
      setDisabled(false)
      navigate('/projects')
    },
    onCancelled: () => {
      setDisabled(false)
    },
    onFail() {
      setDisabled(false)
    },
  })

  useEffect(() => {
    if (transactionStatus?.isPending) {
      setDisabled(true)
    }
  }, [transactionStatus?.isPending])

  const handleChangeMvxToken = async () => {
    setDisabled(true)

    try {
      sessionStorage.setItem(
        'mvxChangesPayload',
        JSON.stringify({
          ...mvxChangesPayload,
          chain: 'multiversx',
          network,
          operation: 'change token properties',
        }),
      )

      const response = await zoid.multiversX.esdtControlChanges(mvxChangesPayload)
      const { transaction, transactionData } = response?.data?.data || {}

      const changeTransaction = {
        data: transactionData.data,
        sender: transactionData.sender.bech32,
        receiver: transactionData.receiver.bech32,
        value: transaction.value,
        gasLimit: transaction.gasLimit,
      }

      const transactionRes = await adapter.multiversx.sendTransaction({
        transactions: changeTransaction,
        transactionsDisplayInfo: {
          processingMessage: 'Processing Change properties transaction',
          errorMessage: 'An error has occurred during transaction',
          successMessage: 'Change properties transaction successful',
        },
        redirectAfterSign: false,
      })
      const payload = sessionStorage.getItem('mvxChangesPayload')
      const parsedPayload = JSON.parse(payload as string)
      sessionStorage.setItem(
        'mvxChangesPayload',
        JSON.stringify({
          ...parsedPayload,
          txHash: transactionRes?.sessionId,
        }),
      )
    } catch (error) {
      console.error('error', error)
      setDisabled(false)
      if (error instanceof Error) {
        toast.error(error?.message, {
          progressClassName: 'simpleBar',
        })
      }
    }
  }

  const renderEsdtProperties = () => {
    return Object.keys(defaultEsdtProperties).map((property) => {
      return (
        <Grid item xs={12} sm={5} md={3} lg={2} key={property}>
          <FormControlLabel
            label={defaultEsdtProperties[property as keyof IEsdtProperties].label}
            sx={{ fontFamily: 'Space Grotesk, sans-serif' }}
            control={
              <Checkbox
                checked={esdtProperties[property as keyof IEsdtProperties].value}
                checkedIcon={<CheckBoxOutlinedIcon />}
                onChange={(e) => handleEsdtPropertiesChange(e, property as keyof IEsdtProperties)}
                inputProps={{ 'aria-label': 'controlled' }}
                sx={{
                  '& .MuiSvgIcon-root': {
                    color: '#2AEAFA',
                  },
                }}
              />
            }
          />
        </Grid>
      )
    })
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '20px',
          width: '100%',
        }}>
        <ConnectMvxInfo infoText="You have to connect your wallet before changing a tokens properties." />
      </Box>
      <Typography
        variant="h5"
        align="center"
        sx={{ fontWeight: '600', fontFamily: 'Space Grotesk, sans-serif' }}>
        Settings
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '10px',
          width: '100%',
        }}>
        <Typography
          variant="h6"
          align="center"
          sx={{
            fontWeight: '500',
            fontFamily: 'Space Grotesk, sans-serif',
          }}>
          Symbol
        </Typography>
        <TextField
          required
          id="outlined-basic"
          placeholder='e.g. "NCT-123abc"'
          variant="outlined"
          value={tokenTicker}
          onChange={handleTokenTickerChange}
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': {
              borderColor: 'transparent',
              color: '#fff',
              background: 'rgba(255, 255, 255, 0.05)',
              fontFamily: 'Space Grotesk, sans-serif',
            },
            '& .MuiFormHelperText-root': {
              color: '#fff',

              '&.Mui-error': {
                color: '#d32f2f',
              },
            },
          }}
        />
      </Box>
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: '500',
              fontFamily: 'Space Grotesk, sans-serif',
            }}>
            Token Configuration
          </Typography>
        </Grid>
        {renderEsdtProperties()}
      </Grid>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '20px',
          width: '100%',
        }}>
        <InfoPaper infoText="The token must be upgradable to perform this operation." />
      </Box>
      <Button
        variant="contained"
        color="success"
        onClick={handleChangeMvxToken}
        disabled={disabled || !tokenTicker || !address}
        sx={{
          fontWeight: '600',
          color: '#181D38',
          background: '#2AEAFA',
          margin: '30px 0 0 0',
          fontFamily: 'Space Grotesk, sans-serif',

          '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.5)',
            background: 'rgba(255, 255, 255, 0.15)',
          },
        }}>
        {disabled ? <Loader></Loader> : null}
        Change token properties
      </Button>
    </>
  )
}

export default EsdtForm
